import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const EventManagement = () => {
  const [eventDays, setEventDays] = useState([]);
  const [scheduleItems, setScheduleItems] = useState({});
  const [editingDay, setEditingDay] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showDayForm, setShowDayForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dayForm, setDayForm] = useState({ day_number: 1, event_date: '', title: '', description: '' });
  const [scheduleForm, setScheduleForm] = useState({ event_day_id: '', time: '', activity: '', description: '' });

  useEffect(() => { fetchEventDays(); }, []);

  const fetchEventDays = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.GET_EVENTS);
      if (response.data.success) {
        setEventDays(response.data.event_days);
        const scheduleMap = {};
        response.data.event_days.forEach(day => { scheduleMap[day.id] = day.schedule || []; });
        setScheduleItems(scheduleMap);
      }
    } catch (error) { console.error('Error fetching event days:', error);
    } finally { setLoading(false); }
  };

  const handleSaveDay = async () => {
    try {
      if (editingDay) await axios.put(API_ENDPOINTS.ADMIN_EVENTS, { ...dayForm, id: editingDay });
      else await axios.post(API_ENDPOINTS.ADMIN_EVENTS, dayForm);
      fetchEventDays();
      setShowDayForm(false);
      setEditingDay(null);
      setDayForm({ day_number: 1, event_date: '', title: '', description: '' });
    } catch (error) { alert('Error saving event day'); }
  };

  const handleDeleteDay = async (id) => {
    if (window.confirm('Are you sure you want to delete this event day?')) {
      try {
        await axios.delete(API_ENDPOINTS.ADMIN_EVENTS, { data: { id } });
        fetchEventDays();
      } catch (error) { alert('Error deleting event day'); }
    }
  };

  const handleSaveSchedule = async () => {
    try {
      if (editingSchedule) await axios.put(API_ENDPOINTS.ADMIN_SCHEDULE, { ...scheduleForm, id: editingSchedule });
      else await axios.post(API_ENDPOINTS.ADMIN_SCHEDULE, scheduleForm);
      fetchEventDays();
      setShowScheduleForm(false);
      setEditingSchedule(null);
      setScheduleForm({ event_day_id: '', time: '', activity: '', description: '' });
    } catch (error) { alert('Error saving schedule item'); }
  };

  const handleDeleteSchedule = async (id) => {
    if (window.confirm('Are you sure you want to delete this schedule item?')) {
      try {
        await axios.delete(API_ENDPOINTS.ADMIN_SCHEDULE, { data: { id } });
        fetchEventDays();
      } catch (error) { alert('Error deleting schedule item'); }
    }
  };

  if (loading) return <div className="loading">Loading events...</div>;

  return (
    <div className="event-management">
      <div className="section-header">
        <h1>Event Program Management</h1>
        <button className="btn-primary" onClick={() => { setEditingDay(null); setDayForm({ day_number: eventDays.length + 1, event_date: '', title: '', description: '' }); setShowDayForm(true); }}>
          <Plus size={18} /> Add Event Day
        </button>
      </div>

      {showDayForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingDay ? 'Edit Event Day' : 'Add Event Day'}</h2>
            <div className="form-group"><label>Day Number</label><input type="number" value={dayForm.day_number} onChange={(e) => setDayForm({...dayForm, day_number: parseInt(e.target.value)})} /></div>
            <div className="form-group"><label>Event Date</label><input type="date" value={dayForm.event_date} onChange={(e) => setDayForm({...dayForm, event_date: e.target.value})} /></div>
            <div className="form-group"><label>Title</label><input type="text" value={dayForm.title} onChange={(e) => setDayForm({...dayForm, title: e.target.value})} /></div>
            <div className="form-group"><label>Description</label><textarea value={dayForm.description} onChange={(e) => setDayForm({...dayForm, description: e.target.value})} rows="3" /></div>
            <div className="modal-actions"><button className="btn-primary" onClick={handleSaveDay}>Save</button><button className="btn-secondary" onClick={() => setShowDayForm(false)}>Cancel</button></div>
          </div>
        </div>
      )}

      {showScheduleForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingSchedule ? 'Edit Schedule Item' : 'Add Schedule Item'}</h2>
            <div className="form-group"><label>Time</label><input type="time" value={scheduleForm.time} onChange={(e) => setScheduleForm({...scheduleForm, time: e.target.value})} /></div>
            <div className="form-group"><label>Activity</label><input type="text" value={scheduleForm.activity} onChange={(e) => setScheduleForm({...scheduleForm, activity: e.target.value})} /></div>
            <div className="form-group"><label>Description</label><textarea value={scheduleForm.description} onChange={(e) => setScheduleForm({...scheduleForm, description: e.target.value})} rows="3" /></div>
            <div className="modal-actions"><button className="btn-primary" onClick={handleSaveSchedule}>Save</button><button className="btn-secondary" onClick={() => setShowScheduleForm(false)}>Cancel</button></div>
          </div>
        </div>
      )}

      {eventDays.map(day => (
        <div key={day.id} className="event-day-card">
          <div className="event-day-header">
            <h2>{day.day_number != 0 ? `Day ${day.day_number}: ` : ''}{new Date(day.event_date).toLocaleDateString()}</h2>
            <div className="action-buttons">
              <button onClick={() => { setEditingDay(day.id); setDayForm({ day_number: day.day_number, event_date: day.event_date, title: day.title || '', description: day.description || '' }); setShowDayForm(true); }}><Edit size={18} /></button>
              <button onClick={() => handleDeleteDay(day.id)}><Trash2 size={18} /></button>
              <button onClick={() => { setScheduleForm({ ...scheduleForm, event_day_id: day.id }); setEditingSchedule(null); setShowScheduleForm(true); }}><Plus size={18} /> Add Schedule</button>
            </div>
          </div>
          {day.description && <p className="day-description">{day.description}</p>}
          <table className="data-table">
            <thead><tr><th>Time</th><th>Activity</th><th>Description</th><th>Actions</th></tr></thead>
            <tbody>
              {scheduleItems[day.id]?.map(item => (
                <tr key={item.id}>
                  <td>{item.time}</td><td><strong>{item.activity}</strong></td><td>{item.description}</td>
                  <td>
                    <button onClick={() => { setEditingSchedule(item.id); setScheduleForm({ event_day_id: day.id, time: item.time, activity: item.activity, description: item.description || '' }); setShowScheduleForm(true); }}><Edit size={16} /></button>
                    <button onClick={() => handleDeleteSchedule(item.id)}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default EventManagement;